<!-- eslint-disable vue/no-v-html -->
<template>
    <div class="sl-article-grid__item" @click="goToDetail(article.code)">
        <!-- Product photo -->
        <photo-carousel
            :photos="article.photos"
            image-class="w-[215px] h-[215px]"
            class="sl-article-grid__carousel"
        />

        <!-- Badges for filters -->
        <div v-if="filterBadges.length" class="sl-article-grid__filters">
            <div
                v-for="filterBadge in filterBadges"
                :key="filterBadge.tag"
                class="sl-article-grid__filter"
                :class="filterBadge.class"
            >
                {{ filterBadge.tag }}
            </div>
        </div>

        <!-- Badges for package quantity -->
        <div v-if="badges.length" class="sl-article-grid__badges">
            <div
                v-for="(badge, index) in badges"
                :key="index"
                class="sl-article-grid__badge"
            >
                {{ badge }}
            </div>
        </div>

        <!-- Badge for new articles -->
        <div v-if="isNew && !hideNewTag" class="sl-article-grid__new">
            <mdi-icon icon="mdiDecagram" />

            <div>NEW</div>
        </div>

        <!-- Availability Communication -->
        <div class="sl-article-grid__availability-communication">
            <template v-if="article.extra.availability_communication">
                {{ article.extra.availability_communication }}
            </template>
        </div>

        <!-- General Communication -->
        <div class="sl-article-grid__general-communication">
            <template v-if="article.extra.general_communication">
                {{ article.extra.general_communication }}
            </template>
        </div>

        <!-- Out of Stock Warning -->
        <div
            v-if="article.extra?.stock_level === 'OUT_OF_STOCK'"
            class="sl-article-grid__out-of-stock"
        >
            {{ t('stock_level.OUT_OF_STOCK.short_warning') }}
        </div>

        <div class="grow"></div>

        <!-- Price -->
        <div
            v-if="store.getters['account/can']('price:view')"
            class="flex justify-between items-center mb-2 w-full"
            :class="{ 'blur': !store.state.pricesVisible }"
        >
            <!-- Total Price -->
            <div class="sl-article-grid__total-price">
                {{ getFormattedTotalPrice(article) }}
            </div>

            <!-- Unit Price -->
            <div v-if="!hideUnitPrice" class="sl-article-grid__unit-price">
                {{ getFormattedUnitPrice(article) }}
            </div>
        </div>

        <!-- Footer -->
        <div class="sl-article-grid__footer">
            <div class="sl-article-grid__footer-1">
                <!-- Label -->
                <div class="sl-article-grid__label">
                    {{ article.label }}
                </div>

                <!-- Short Description (if any) -->
                <tippy>
                    <mdi-icon
                        v-if="article.short_description"
                        icon="mdiInformationOutline"
                        class="cursor-help ml-1 w-4 h-4 text-gray-500"
                    />

                    <template #content>
                        <div
                            class="sl-article-grid__description"
                            v-html="nl2br(article.short_description)"
                        >
                        </div>
                    </template>
                </tippy>

                <div class="grow"></div>

                <!-- "Add to Basket" button -->
                <div class="sl-article-grid__actions">
                    <div class="relative">
                        <div
                            v-if="basketQuantity"
                            class="sl-article-grid__basket-quantity"
                        >
                            {{ basketQuantity }}
                        </div>

                        <tippy>
                            <mdi-icon
                                icon="mdiBasket"
                                class="sl-article-grid__basket"
                                :class="{
                                    'sl-article-grid__basket--loading': addingToBasket,
                                    'sl-article-grid__basket--disabled': basketDisabled,
                                }"
                                @click.stop="addToBasket(article)"
                            />

                            <template #content>
                                <template v-if="basketDisabled">
                                    {{ article.extra.availability_communication }}
                                </template>

                                <template v-else>
                                    {{ t('common.add_to_basket') }}
                                </template>
                            </template>
                        </tippy>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useArticleGridComposable } from '@/composables/ArticleGridComposable';
import PhotoCarousel from '@/components/PhotoCarousel.vue';

const props = defineProps({
    article:
    {
        type:     Object,
        required: true,
    },

    filterBadges:
    {
        type:    Array,
        default: () => [],
    },

    badges:
    {
        type:    Array,
        default: () => [],
    },

    hideUnitPrice:
    {
        type:    Boolean,
        default: false,
    },

    hideNewTag:
    {
        type:    Boolean,
        default: false,
    },
});

const { t } = useI18n({ useScope: 'global' });
const store = useStore();
const {
    addingToBasket,
    basketDisabled,
    isNew,
    getFormattedUnitPrice,
    getFormattedTotalPrice,
    addToBasket,
    nl2br,
    goToDetail,
} = useArticleGridComposable(computed(() => props.article));

const basketQuantity = computed(() =>
{
    const lines = store.getters['basket/getLinesByArticleCode'](props.article.code);
    return lines.reduce((acc, l) => acc + l.quantity, 0);
});
</script>
